import { fetchJson } from "../lib/fetchJson.js"
import { setElementInnerHTML } from "../lib/utils.js"

/** After `sam deploy`, replace with InterestEndpoint stack output. */
export const INTEREST_CAPTURE_URL =
  "https://vygbnfddmh.execute-api.us-west-2.amazonaws.com/Prod/interest"

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface InterestListEntry {
  variantId: string
  sku: string
  name: string
  el: HTMLElement
  qtyInput: HTMLInputElement
}

class FFInterestList extends HTMLElement {
  DOM: {
    form: HTMLFormElement | null
    email: HTMLInputElement | null
    honorific: HTMLInputElement | null
    submitBtn: HTMLButtonElement | null
    status: HTMLElement | null
    entries: InterestListEntry[]
  }
  submitting = false

  constructor() {
    super()
    this.DOM = {
      form: this.querySelector(".js-interestForm"),
      email: this.querySelector(".js-interestEmail"),
      honorific: this.querySelector(".js-interestHonorific"),
      submitBtn: this.querySelector(".js-interestSubmit"),
      status: this.querySelector(".js-interestStatus"),
      entries: [...this.querySelectorAll(".js-entry")].reduce((acc, cur) => {
        if (!(cur instanceof HTMLElement)) return acc
        const variantId = cur.getAttribute("data-value")
        if (!variantId) return acc
        const qtyInput = cur.querySelector(".qty")
        if (!(qtyInput instanceof HTMLInputElement)) return acc
        return [
          ...acc,
          {
            variantId,
            sku: cur.getAttribute("data-sku") || "",
            name: cur.getAttribute("data-name") || "",
            el: cur,
            qtyInput,
          },
        ]
      }, [] as InterestListEntry[]),
    }
    this.events()
    this.updateSubmitEnabled()
  }

  events() {
    this.DOM.form?.addEventListener("submit", this.onSubmit.bind(this))
    this.DOM.email?.addEventListener("input", this.updateSubmitEnabled.bind(this))
    this.DOM.email?.addEventListener("change", this.updateSubmitEnabled.bind(this))
    this.DOM.entries.forEach((entry) => {
      entry.qtyInput.addEventListener("input", this.updateSubmitEnabled.bind(this))
      entry.qtyInput.addEventListener("change", this.updateSubmitEnabled.bind(this))
    })
  }

  isEmailValid() {
    const email = (this.DOM.email?.value || "").trim()
    return EMAIL_RE.test(email)
  }

  getRequestedItems() {
    return this.DOM.entries.reduce(
      (acc, cur) => {
        const quantity = parseInt(cur.qtyInput.value, 10)
        if (!Number.isInteger(quantity) || quantity < 1) return acc
        return [
          ...acc,
          {
            sku: cur.sku,
            name: cur.name,
            quantity,
            variantId: cur.variantId,
          },
        ]
      },
      [] as { sku: string; name: string; quantity: number; variantId: string }[],
    )
  }

  isFormValid() {
    return this.isEmailValid() && this.getRequestedItems().length > 0
  }

  updateSubmitEnabled() {
    if (!this.DOM.submitBtn || this.submitting) return
    this.DOM.submitBtn.disabled = !this.isFormValid()
  }

  setStatus(message: string, kind: "success" | "error" | "info") {
    const el = this.DOM.status
    if (!el) return
    el.hidden = false
    el.className = `js-interestStatus interestForm__status interestForm__status--${kind}`
    setElementInnerHTML(el, message)
  }

  emptyQuantities() {
    this.DOM.entries.forEach((entry) => {
      entry.qtyInput.value = "0"
    })
  }

  async onSubmit(e: SubmitEvent) {
    e.preventDefault()
    if (this.submitting || !this.isFormValid()) return

    const email = (this.DOM.email?.value || "").trim()
    const items = this.getRequestedItems()

    this.submitting = true
    if (this.DOM.submitBtn) this.DOM.submitBtn.disabled = true
    this.setStatus("Sending…", "info")

    try {
      await fetchJson(INTEREST_CAPTURE_URL, {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
        body: JSON.stringify({
          email,
          items,
          honorific: this.DOM.honorific?.value || "",
          source: "store-reservation",
        }),
      })
      this.emptyQuantities()
      if (this.DOM.email) this.DOM.email.value = ""
      if (this.DOM.form) {
        setElementInnerHTML(
          this.DOM.form,
          "<p class='success'>Thanks — we&rsquo;ve recorded your interest. We&rsquo;ll follow up by email when we&rsquo;re ready.</p>",
        )
      }
    } catch {
      this.setStatus("Something went wrong. Please try again.", "error")
      this.submitting = false
      this.updateSubmitEnabled()
    }
  }
}

customElements.define("ff-interest-list", FFInterestList)
